import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { CondicionesCancelacion, HistorialReserva } from '../../../api/ops/reservasGestion';
import type { Reserva } from '../../../api/ops/reservas';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Segmentos } from '../../../componentes/Segmentos';
import { Ventana } from '../../../componentes/Ventana';
import { dinero } from '../../../logica/dinero';
import { avisoBreveDeError } from '../../../logica/estados';
import { usePermisos } from '../../../permisos/permisos';
import { Contador } from '../../../componentes/Contador';
import { dondeEs, nombreDe, useNombres, type ContextoReservas } from '../contexto';
import { diaLargo, horaEn } from '../fechas';
import { CANAL, ESTADO, mayuscula } from '../textos';
import { PestanaPagos, PestanaPase } from './PagosYPase';
import '../../sala/sala.css';

/**
 * 8.3 Detalle de la reserva (RV Panel «ficha», `?reserva=`): Resumen (cuándo, dónde, personas, cliente, «Alergias o
 * intolerancias» en ámbar mientras dura la reserva, G192, notas que se guardan con `updateReservationNotes`), Cambiar
 * (fecha, hora y personas con `changeReservation`; si el recurso cambia, `409 confirmation_required` y «Cambiar
 * igualmente», G16; al cambiar fecha, hora o personas se anula el pase y se emite otro, G466) y Historial
 * (`listReservationHistory`). Cancelar con lo que corresponde (`getReservationCancellationTerms`), solo con
 * `reservas.reservas.cancelar` (Recepción no). Pagos y Pase, en `PagosYPase.tsx`.
 */
interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    soloLectura: boolean;
    reservaId: string;
    contexto: ContextoReservas;
    onCerrar: () => void;
    navegar?: ((ruta: string) => void) | undefined;
}

const PERMISOS = { modificar: 'reservas.reservas.modificar', cancelar: 'reservas.reservas.cancelar' } as const;
const VIVAS = ['pending_payment', 'confirmed', 'arrived'];

const HISTORIA: Readonly<Record<string, string>> = {
    created: 'Reserva creada',
    confirmed: 'Confirmada',
    changed: 'Cambiada',
    resource: 'Cambio de sitio',
    status: 'Cambio de estado',
    payment: 'Pago',
    note: 'Nota',
};

const DINERO: Readonly<Record<string, string>> = {
    full_refund: 'Se devuelve todo lo pagado',
    partial_refund: 'Se devuelve una parte',
    retain: 'Se queda lo pagado',
    late_fee: 'Se cobra la cancelación tardía',
    nothing_to_pay: 'No hay nada pagado',
};

export function FichaReserva({ ops, negocio, movil, soloLectura, reservaId, contexto, onCerrar, navegar }: Props) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const [carga, setCarga] = useState<{ id: string; reserva: Reserva | null; error: boolean } | null>(null);
    const [intento, setIntento] = useState(0);
    const [pestana, setPestana] = useState(0);
    const [historial, setHistorial] = useState<{ id: string; datos: HistorialReserva['data'] } | null>(null);
    const [notas, setNotas] = useState<{ cliente: string; interna: string } | null>(null);
    const [cambio, setCambio] = useState<{ dia: string; hora: string; personas: number } | null>(null);
    const [igualmente, setIgualmente] = useState(false);
    const [cancelar, setCancelar] = useState<{ condiciones: CondicionesCancelacion | null; motivo: string } | null>(null);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.getReservation(negocio, reservaId)
            .then((x) => { if (vivo) { setCarga({ id: reservaId, reserva: x.datos, error: false }); } })
            .catch(() => { if (vivo) { setCarga({ id: reservaId, reserva: null, error: true }); } });
        return () => { vivo = false; };
    }, [ops, negocio, reservaId, intento]);

    useEffect(() => {
        if (pestana !== 4) {
            return;
        }
        let vivo = true;
        ops.listReservationHistory(negocio, reservaId, { limit: 50 })
            .then((h) => { if (vivo) { setHistorial({ id: reservaId, datos: h.data }); } })
            .catch(() => { if (vivo) { setHistorial({ id: reservaId, datos: [] }); } });
        return () => { vivo = false; };
    }, [ops, negocio, reservaId, pestana]);

    const r = carga?.id === reservaId ? carga.reserva : null;
    const nombres = useNombres(ops, negocio, r?.customer_id == null ? [] : [r.customer_id], permisos.puede('clientes.fichas.ver'));
    const viva = r !== null && VIVAS.includes(r.status);
    const modifica = permisos.puede(PERMISOS.modificar) && !soloLectura && viva;
    const puedeCancelar = permisos.puede(PERMISOS.cancelar) && !soloLectura && viva;
    const [texto, estado] = r === null ? ['', 'neutral' as const] : ESTADO[r.status];
    const nota = notas ?? { cliente: r?.customer_note ?? '', interna: r?.staff_note ?? '' };
    const cambiar = cambio ?? (r === null ? null : { dia: r.local_date, hora: r.local_time ?? horaEn(r.starts_at, contexto.zona), personas: r.party_size });
    const recargar = () => { setIntento((i) => i + 1); };

    const error = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };

    const guardarNotas = () => {
        if (r === null) {
            return;
        }
        setOcupado(true);
        ops.updateReservationNotes(negocio, r, { customer_note: nota.cliente.trim() === '' ? null : nota.cliente.trim(), staff_note: nota.interna.trim() === '' ? null : nota.interna.trim() })
            .then(() => { avisar({ tipo: 'ok', texto: 'Notas guardadas.' }); setNotas(null); recargar(); })
            .catch((e: unknown) => { error(e, 'No se han podido guardar las notas.'); })
            .finally(() => { setOcupado(false); });
    };

    const hacerCambio = (confirmar: boolean) => {
        if (r === null || cambiar === null) {
            return;
        }
        setOcupado(true);
        ops.changeReservation(negocio, r, { when: { local_date: cambiar.dia, local_time: cambiar.hora }, party_size: cambiar.personas, confirm_different_resource: confirmar })
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Reserva cambiada. Le enviamos el pase nuevo.' });
                setCambio(null);
                setIgualmente(false);
                setPestana(0);
                recargar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'confirmation_required') {
                    setIgualmente(true);
                } else if (e instanceof ErrorApi && e.codigo === 'capacity_unavailable') {
                    avisar({ tipo: 'error', texto: 'A esa hora no hay sitio. Prueba con otra.' });
                } else {
                    error(e, 'No se ha podido cambiar la reserva.');
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const abrirCancelar = () => {
        setCancelar({ condiciones: null, motivo: '' });
        ops.getReservationCancellationTerms(negocio, reservaId)
            .then((c) => { setCancelar((x) => (x === null ? null : { ...x, condiciones: c })); })
            .catch(() => undefined);
    };

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            icono="calendar-check"
            eyebrow="Reserva"
            titulo={r === null ? 'Reserva' : nombreDe(r, nombres)}
            subtitulo={r === null ? '' : `${r.reference} • ${CANAL[r.channel] ?? r.channel}`}
            {...(r === null ? {} : { chip: texto, chipEstado: estado })}
            {...(pestana === 1 && modifica ? { accion: 'Cambiar', accionIcono: 'check' as const, ocupado, onAccion: () => { hacerCambio(false); } } : {})}
        >
            {r === null ? (
                <p className="rv-sl-nota">{carga?.error === true ? 'No se ha podido abrir la reserva.' : 'Cargando…'}</p>
            ) : (
                <div className="rv-sl-form">
                    <Segmentos etiqueta="Partes de la reserva" movil={movil} items={[{ etiqueta: 'Resumen', icono: 'info' }, { etiqueta: 'Cambiar', icono: 'calendar-days' }, { etiqueta: 'Pagos', icono: 'credit-card' }, { etiqueta: 'Pase', icono: 'ticket' }, { etiqueta: 'Historial', icono: 'clock' }]} valor={pestana} onCambio={setPestana} />
                    {pestana === 0 && (
                        <>
                            {r.health_info != null && (
                                <p className="rv-sl-alergias" role="note"><Icono nombre="triangle-alert" /><span>{`Alergias o intolerancias: ${r.health_info.text}`}</span></p>
                            )}
                            <dl className="rv-rs-presupuesto">
                                <div><dt>Cuándo</dt><dd>{`${mayuscula(diaLargo(r.local_date))}, ${r.local_time ?? horaEn(r.starts_at, contexto.zona)}`}</dd></div>
                                <div><dt>Dónde</dt><dd>{dondeEs(contexto, r) || '—'}</dd></div>
                                <div><dt>Personas</dt><dd>{r.party_size}</dd></div>
                                {r.total !== undefined && <div><dt>Total</dt><dd>{dinero(r.total)}</dd></div>}
                                {r.free_cancellation_until != null && <div><dt>Cancelación gratis hasta</dt><dd>{`${mayuscula(diaLargo(r.free_cancellation_until.slice(0, 10)))}, ${horaEn(r.free_cancellation_until, contexto.zona)}`}</dd></div>}
                                {r.needs_relocation && <div><dt>Sitio</dt><dd><Chip estado="problem">Para recolocar</Chip></dd></div>}
                            </dl>
                            <Campo etiqueta="Lo que pide el cliente" valor={nota.cliente} onChange={(v) => { setNotas({ ...nota, cliente: v }); }} max={500} filas={2} bloqueado={!modifica} />
                            <Campo etiqueta="Nota interna" valor={nota.interna} onChange={(v) => { setNotas({ ...nota, interna: v }); }} max={500} filas={2} bloqueado={!modifica} />
                            {notas !== null && modifica && <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={guardarNotas}><Icono nombre="check" /><span>Guardar las notas</span></button>}
                            {puedeCancelar && <button type="button" className="rv-b rv-b-des" onClick={abrirCancelar}><Icono nombre="x" /><span>Cancelar la reserva</span></button>}
                        </>
                    )}
                    {pestana === 1 && cambiar !== null && (
                        modifica ? (
                            <>
                                <p className="rv-sl-nota">Si cambias la fecha, la hora o las personas, el pase anterior deja de valer y le enviamos uno nuevo.</p>
                                <Fecha etiqueta="Día" valor={cambiar.dia} onChange={(v) => { setCambio({ ...cambiar, dia: v }); }} />
                                <Campo etiqueta="Hora (HH:MM)" valor={cambiar.hora} onChange={(v) => { setCambio({ ...cambiar, hora: v }); }} max={5} />
                                <Contador etiqueta="Personas" ayuda="" valor={cambiar.personas} onCambio={(n) => { setCambio({ ...cambiar, personas: n }); }} />
                            </>
                        ) : (
                            <p className="rv-sl-nota">{viva ? 'No puedes cambiar esta reserva.' : 'Esta reserva ya no se puede cambiar.'}</p>
                        )
                    )}
                    {pestana === 2 && <PestanaPagos ops={ops} negocio={negocio} reserva={r} contexto={contexto} soloLectura={soloLectura} navegar={navegar} onCambio={recargar} />}
                    {pestana === 3 && <PestanaPase ops={ops} negocio={negocio} reserva={r} contexto={contexto} soloLectura={soloLectura} onCambio={recargar} />}
                    {pestana === 4 && (
                        historial?.id !== reservaId ? <p className="rv-sl-nota">Cargando…</p> : (
                            <ul className="rv-sl-desplazadas">
                                {historial.datos.map((h, i) => (
                                    <li key={`${h.at}${String(i)}`}>
                                        <span>{`${mayuscula(diaLargo(h.at.slice(0, 10)))}, ${horaEn(h.at, contexto.zona)}`}</span>
                                        <strong>{`${HISTORIA[h.kind] ?? h.kind}${h.reason == null ? '' : ` · ${h.reason}`}`}</strong>
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                </div>
            )}
            <Ventana abierta={igualmente} titulo="Cambia el sitio" texto="Con el cambio, la reserva pasa a otra zona o a otro recurso distinto del que reservó. ¿Lo cambias igualmente?" accion="Cambiar igualmente" ocupado={ocupado} onAccion={() => { hacerCambio(true); }} onCerrar={() => { setIgualmente(false); }} />
            <Ventana
                abierta={cancelar !== null}
                titulo="¿Cancelar la reserva?"
                texto={cancelar?.condiciones == null ? 'Calculando lo que pasa con el dinero…' : `${DINERO[cancelar.condiciones.outcome] ?? ''}${cancelar.condiciones.refund_amount === undefined ? '' : ` (${dinero(cancelar.condiciones.refund_amount)})`}. Al cliente le avisamos.`}
                accion="Cancelar la reserva"
                accionIcono="x"
                cancelar="Volver"
                ocupado={ocupado}
                inactiva={(cancelar?.motivo.trim() ?? '') === '' || cancelar?.condiciones?.can_cancel === false}
                onAccion={() => {
                    if (cancelar === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.cancelReservation(negocio, reservaId, cancelar.motivo.trim())
                        .then(() => { avisar({ tipo: 'ok', texto: 'Reserva cancelada.' }); setCancelar(null); recargar(); })
                        .catch((e: unknown) => { error(e, 'No se ha podido cancelar.'); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setCancelar(null); }}
            >
                <Campo etiqueta="Motivo" valor={cancelar?.motivo ?? ''} onChange={(v) => { setCancelar((x) => (x === null ? null : { ...x, motivo: v })); }} max={500} />
            </Ventana>
        </PanelLateral>
    );
}
